/**
 * Unified Diagram Controls - Filter Chips Component
 * Issue #245: Standardize Diagram Controls Across All Renderers
 *
 * A chip-based filter component for toggling filter values.
 */

import { memo } from 'react';
import type { FilterDefinition } from './types';

interface FilterChipsProps {
  filter: FilterDefinition;
  activeValues: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  className?: string;
}

/**
 * Filter chips component for toggling filter values
 */
export const FilterChips = memo(function FilterChips({
  filter,
  activeValues,
  onToggle,
  onClear,
  className = '',
}: FilterChipsProps) {
  const hasActiveFilters = activeValues.size > 0;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
          {filter.label}
        </span>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {filter.options.map((option) => {
          const isActive = activeValues.has(option.value);

          return (
            <button
              key={option.value}
              onClick={() => onToggle(option.value)}
              title={option.description}
              className={`
                px-2 py-1 text-xs rounded-full border transition-all
                ${isActive
                  ? 'ring-2 ring-offset-1 ring-blue-500 dark:ring-blue-400'
                  : 'opacity-70 hover:opacity-100'
                }
              `}
              style={option.color ? {
                backgroundColor: `${option.color}20`,
                borderColor: option.color,
                color: option.color,
              } : undefined}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
});
